/**
 * What a profile tab shows until the candidate has loaded: the page's own shape in
 * shimmering placeholders, a ring sweeping round the avatar, and a caption walking
 * through what's being fetched. The placeholders wait a beat before appearing, so a
 * fast load never flashes them. Motion stops for viewers who ask for reduced motion
 * (and in tests, where matchMedia is missing). Not loading and an error: the message.
 */
import { useEffect, useState } from "react";
import { keyframes } from "@emotion/react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ProfileSidebar, { profileTabRowSx } from "./ProfileSidebar";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;
const breathe = keyframes`
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 1; }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const REDUCED = "@media (prefers-reduced-motion: reduce)";

export const PROFILE_LOADING_STEPS = [
  "Pulling up the profile",
  "Reading work history",
  "Gathering interviews",
  "Checking scores and notes",
];
/** How long a load must take before the placeholders appear. */
export const PROFILE_LOADING_DELAY_MS = 150;
const STEP_MS = 1400;

function canAnimate(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function AvatarRing() {
  return (
    <Box sx={{ position: "relative", width: 76, height: 76, flexShrink: 0 }}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: "conic-gradient(from 0deg, #4F46E5, #3B82F6, #22C55E, #4F46E5)",
          WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
          mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
          animation: `${spin} 1.6s linear infinite`,
          [REDUCED]: { animation: "none" },
        }}
      />
      <Skeleton variant="circular" animation="wave" width={64} height={64} sx={{ position: "absolute", top: 6, left: 6 }} />
    </Box>
  );
}

function Caption() {
  const animate = canAnimate();
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!animate) return;
    const t = setInterval(() => setStep((s) => (s + 1) % PROFILE_LOADING_STEPS.length), STEP_MS);
    return () => clearInterval(t);
  }, [animate]);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, color: "text.secondary" }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "#4F46E5",
          animation: `${breathe} 1.2s ease-in-out infinite`,
          [REDUCED]: { animation: "none" },
        }}
      />
      <Typography
        key={step}
        sx={{ fontSize: 13, animation: `${rise} 0.3s ease-out`, [REDUCED]: { animation: "none" } }}
      >
        {PROFILE_LOADING_STEPS[step]}…
      </Typography>
    </Box>
  );
}

function Line({ width, height = 16 }: { width: number | string; height?: number }) {
  return <Skeleton variant="rounded" animation="wave" width={width} height={height} sx={{ borderRadius: 1 }} />;
}

function Placeholders() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
      <Paper sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", gap: 2.5, alignItems: "center" }}>
          <AvatarRing />
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            <Line width="36%" height={24} />
            <Line width="58%" />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
              {[84, 112, 70, 96].map((w) => (
                <Skeleton key={w} variant="rounded" animation="wave" width={w} height={24} sx={{ borderRadius: 3 }} />
              ))}
            </Box>
          </Box>
        </Box>
        <Caption />
      </Paper>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 2 }}>
        <Paper sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <Line width={150} height={18} />
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: "flex", gap: 1.5 }}>
              <Skeleton variant="rounded" animation="wave" width={40} height={40} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Line width={`${52 - i * 8}%`} />
                <Line width="34%" height={12} />
                <Line width="88%" height={12} />
              </Box>
            </Box>
          ))}
        </Paper>
        <Paper sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Line width={120} height={18} />
          {["90%", "70%", "80%", "55%", "75%"].map((w, i) => (
            <Line key={i} width={w} height={12} />
          ))}
        </Paper>
      </Box>
    </Box>
  );
}

export default function ProfileLoadingState({
  loading,
  error,
  withSidebar = true,
}: {
  loading: boolean;
  error?: string | null;
  /** Profile tabs show their nav rail beside the content; the pop-up profile has none. */
  withSidebar?: boolean;
}) {
  // No data and no error means the first request is about to start (it's dispatched
  // in an effect after the first render), so that reads as loading too.
  const isLoading = loading || !error;
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => setShow(true), PROFILE_LOADING_DELAY_MS);
    return () => clearTimeout(t);
  }, [isLoading]);

  if (!isLoading) {
    return (
      <Box sx={{ ...profileTabRowSx, height: "100%", overflow: "auto" }}>
        {withSidebar && <ProfileSidebar />}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, p: 6, color: "text.secondary" }}>
          <PersonSearchOutlinedIcon sx={{ fontSize: 40, opacity: 0.6 }} />
          <Typography sx={{ color: "text.secondary" }}>{error || "Profile not found"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading profile"
      sx={{ ...profileTabRowSx, height: "100%", overflow: "auto" }}
    >
      {withSidebar && <ProfileSidebar />}
      {show ? <Placeholders /> : <Box sx={{ flex: 1 }} />}
    </Box>
  );
}
